/**
 * Vegetation for the LoD2 view: trees and shrubs as simple models (trunk + crown), lawn as flat
 * areas. The items arrive ready from the server (`/city-objects/vegetation`, tri-server
 * docs/vegetation-3d.md) — positions in WGS84, crown radius and height in metres.
 *
 * Modelled, not measured: outside NRW the heights are estimated from the crown diameter, and the
 * view says so. One InstancedMesh per part, so a few thousand trees cost a handful of draw calls.
 */
import * as THREE from "three";
import earcut from "earcut";

export interface VegetationItem {
  type: "tree" | "shrub";
  lon: number;
  lat: number;
  crownRadiusM: number;
  heightM: number;
  heightEstimated: boolean;
  vitality: "vital" | "stressed" | "critical" | "unknown";
}

export interface LawnArea {
  ring: number[][];
}

/** How a plant is built, in metres above the ground. Pure, so it can be tested without WebGL. */
export interface PlantShape {
  trunk: { radius: number; height: number } | null;
  crown: { radius: number; verticalRadius: number; centreHeight: number };
}

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

/**
 * Higher than this is a raster artefact, not a tree (tri-server MAX_PLAUSIBLE_HEIGHT_M): a nodata
 * value of 9999 came through as a height and drew 10 km trunks. Results stored before the server
 * caught it still carry it, so the view checks too and falls back to the same estimate.
 */
export const MAX_PLAUSIBLE_HEIGHT_M = 60;
const estimatedHeight = (type: VegetationItem["type"], crownRadiusM: number) =>
  type === "tree" ? clamp(2.6 * crownRadiusM, 4, 30) : clamp(1.2 * crownRadiusM, 0.8, 2.5);

export const plantShape = (item: VegetationItem): PlantShape => {
  const radius = Math.max(item.crownRadiusM, 0.3);
  const plausible = Number.isFinite(item.heightM) && item.heightM <= MAX_PLAUSIBLE_HEIGHT_M;
  const height = Math.max(plausible ? item.heightM : estimatedHeight(item.type, radius), 0.5);
  if (item.type === "shrub") {
    // A bush is all crown: a flattened ball sitting on the ground.
    const verticalRadius = height / 2;
    return { trunk: null, crown: { radius, verticalRadius, centreHeight: verticalRadius } };
  }
  // A tree: the crown takes the upper half or so, the trunk carries it. Detection often finds only
  // part of a tall tree's crown, and taken literally that drew 20 m poles with a knob on top. So the
  // crown is at least about a sixth of the height wide and about 55 % of it deep — a modelled tree,
  // not a measured one, but one that reads as a tree.
  const crownRadius = Math.max(radius, height * 0.16);
  const verticalRadius = clamp(height * 0.28, 0.5, height / 2);
  const centreHeight = height - verticalRadius;
  return {
    trunk: { radius: clamp(crownRadius * 0.08, 0.1, 0.6), height: Math.max(centreHeight, 0.3) },
    crown: { radius: crownRadius, verticalRadius, centreHeight },
  };
};

/**
 * Muted, natural greens (sRGB, as a designer picks them). Converted to linear before use: Threebox
 * renders with sRGB output, and a hex value handed over unconverted comes out lighter and harsher
 * — the first version's pure greens read as signal colours, not as trees.
 */
export const VITALITY_COLOURS: Record<VegetationItem["vitality"], number> = {
  vital: 0x4f7a3a,
  stressed: 0x7f8440,
  critical: 0x8a6c47,
  unknown: 0x56783f,
};
const SHRUB_COLOUR = 0x66854a;
const TRUNK_COLOUR = 0x5b4636;
const LAWN_COLOUR = 0x8aa266;

type Project = (lon: number, lat: number, altitude: number) => number[];

/** Pure: a stable pseudo-random number in [0, 1) from a position — the same tree looks the same on every load. */
export const hash01 = (lon: number, lat: number, salt = 0): number => {
  const v = Math.sin(lon * 12.9898 + lat * 78.233 + salt * 37.719) * 43758.5453;
  return v - Math.floor(v);
};

/** Pure: brightness and hue spread per plant, so a stand of trees is not one flat colour. */
export const colourJitter = (lon: number, lat: number) => ({
  lightness: 0.82 + 0.3 * hash01(lon, lat, 1),
  hueShift: (hash01(lon, lat, 2) - 0.5) * 0.04,
});

/**
 * One irregular crown shape, shared by all instances: an icosphere whose radius is nudged by a few
 * overlapping waves. Each instance turns it differently, so neighbours do not look cloned.
 */
const lumpyCrownGeometry = () => {
  const geometry = new THREE.IcosahedronGeometry(1, 2);
  const position = geometry.attributes.position;
  const v = new THREE.Vector3();
  for (let i = 0; i < position.count; i++) {
    v.fromBufferAttribute(position, i).normalize();
    const bump =
      0.1 * Math.sin(5.1 * v.x + 1.3) * Math.cos(4.3 * v.y) +
      0.07 * Math.sin(7.7 * v.z + 2.1 * v.x) +
      0.05 * Math.cos(9.3 * v.y + 3.7 * v.z);
    v.multiplyScalar(1 + bump);
    position.setXYZ(i, v.x, v.y, v.z);
  }
  geometry.computeVertexNormals();
  return geometry;
};

/**
 * Foliage texture drawn at runtime — clumps of lighter and darker leaves on a light ground. It is
 * near-white on average, so the per-instance colour still decides the tone; the texture only adds
 * the grain that makes a crown read as leaves. No image file, no licence question.
 */
let foliageTexture: any = null;
const foliage = () => {
  if (foliageTexture || typeof document === "undefined") return foliageTexture;
  const size = 256;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const context = canvas.getContext("2d");
  if (!context) return null;
  context.fillStyle = "rgb(205,205,205)";
  context.fillRect(0, 0, size, size);
  let seed = 7;
  const random = () => ((seed = (seed * 16807) % 2147483647) / 2147483647);
  for (let i = 0; i < 1400; i++) {
    const x = random() * size;
    const y = random() * size;
    const r = 2 + random() * 6;
    const shade = Math.round(140 + random() * 115);
    context.fillStyle = `rgba(${shade},${shade},${shade},${0.55 + random() * 0.4})`;
    context.beginPath();
    context.ellipse(x, y, r, r * (0.5 + random() * 0.5), random() * Math.PI, 0, 2 * Math.PI);
    context.fill();
  }
  foliageTexture = new THREE.CanvasTexture(canvas);
  foliageTexture.wrapS = THREE.RepeatWrapping;
  foliageTexture.wrapT = THREE.RepeatWrapping;
  foliageTexture.repeat.set(3, 2);
  return foliageTexture;
};

const linear = (hex: number) => new THREE.Color(hex).convertSRGBToLinear();

/** One leaf clump of a crown, in metres relative to the crown centre. */
export interface Clump {
  dx: number;
  dy: number;
  dz: number;
  radius: number;
  verticalRadius: number;
  /** Lighter on top, darker below — where the sun reaches and where it does not. */
  shade: number;
}

/**
 * Pure: a crown as a few overlapping clumps instead of one ball. A central clump carries the mass,
 * the others sit around and above it, so the outline is lobed like a real crown. Placement is fixed
 * per position (`hash01`), and the clumps stay inside the crown's measured extent — the crown keeps
 * its radius and height, it only stops being a sphere.
 */
export const crownClumps = (item: VegetationItem, shape: PlantShape): Clump[] => {
  const { radius, verticalRadius } = shape.crown;
  const count = item.type === "shrub" ? 3 : radius > 4 ? 6 : 5;
  const clumpRadius = radius * (item.type === "shrub" ? 0.62 : 0.55);
  const clumpVertical = Math.min(verticalRadius * 0.7, clumpRadius * 1.1);
  const reach = Math.max(radius - clumpRadius, 0);
  const lift = Math.max(verticalRadius - clumpVertical, 0);
  const clumps: Clump[] = [{ dx: 0, dy: 0, dz: 0, radius: clumpRadius * 1.15, verticalRadius: clumpVertical * 1.1, shade: 1 }];
  for (let i = 1; i < count; i++) {
    const angle = (i / (count - 1)) * 2 * Math.PI + hash01(item.lon, item.lat, 10 + i) * 0.9;
    const out = reach * (0.7 + 0.3 * hash01(item.lon, item.lat, 20 + i));
    const up = lift * (hash01(item.lon, item.lat, 30 + i) * 1.6 - 0.6);
    const size = 0.85 + 0.25 * hash01(item.lon, item.lat, 40 + i);
    clumps.push({
      dx: Math.cos(angle) * out,
      dy: Math.sin(angle) * out,
      dz: up,
      radius: clumpRadius * size,
      verticalRadius: clumpVertical * size,
      shade: 0.88 + 0.22 * (lift > 0 ? (up + lift) / (2 * lift) : 0.5),
    });
  }
  return clumps;
};

/**
 * Build the vegetation as one group for Threebox's `world`. `project` is the view's own
 * `projectToWorld`; `unitsPerMetre` turns metres into world units at this latitude (horizontal and
 * vertical scale agree there — the Mercator world is conformal).
 */
export const buildVegetationGroup = (
  items: VegetationItem[],
  lawn: LawnArea[],
  project: Project,
  unitsPerMetre: number,
): any => {
  const group = new THREE.Group();
  group.name = "vegetation";

  // Lawn first and slightly above the ground, so the base map's ground does not flicker through.
  const lawnMaterial = new THREE.MeshLambertMaterial({ color: linear(LAWN_COLOUR), transparent: true, opacity: 0.5, side: THREE.DoubleSide });
  lawn.forEach((area) => {
    const world = area.ring.map(([lon, lat]) => project(lon, lat, 0.05));
    const indices = earcut(world.flatMap((p) => [p[0], p[1]]));
    if (!indices.length) return;
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute("position", new THREE.BufferAttribute(new Float32Array(world.flat()), 3));
    geometry.setIndex(indices);
    geometry.computeVertexNormals();
    const lawnMesh = new THREE.Mesh(geometry, lawnMaterial);
    lawnMesh.receiveShadow = true;
    group.add(lawnMesh);
  });

  const shapes = items.map((item) => ({ item, shape: plantShape(item) }));
  const trunks = shapes.filter((entry) => entry.shape.trunk);
  const matrix = new THREE.Matrix4();
  const position = new THREE.Vector3();
  const scale = new THREE.Vector3();
  const upright = new THREE.Quaternion();
  const turn = new THREE.Quaternion();
  const zAxis = new THREE.Vector3(0, 0, 1);
  const colour = new THREE.Color();
  const hsl = { h: 0, s: 0, l: 0 };

  if (trunks.length) {
    // A unit cylinder standing on the z axis (three.js builds it along y).
    const geometry = new THREE.CylinderGeometry(0.7, 1, 1, 7).rotateX(Math.PI / 2);
    const mesh = new THREE.InstancedMesh(geometry, new THREE.MeshLambertMaterial({ color: linear(TRUNK_COLOUR) }), trunks.length);
    trunks.forEach(({ item, shape }, index) => {
      const trunk = shape.trunk!;
      const [x, y, z] = project(item.lon, item.lat, trunk.height / 2);
      position.set(x, y, z);
      scale.set(trunk.radius * unitsPerMetre, trunk.radius * unitsPerMetre, trunk.height * unitsPerMetre);
      mesh.setMatrixAt(index, matrix.compose(position, upright, scale));
    });
    mesh.instanceMatrix.needsUpdate = true;
    mesh.castShadow = true;
    group.add(mesh);
  }

  if (shapes.length) {
    const clumpsPerPlant = shapes.map(({ item, shape }) => crownClumps(item, shape));
    const total = clumpsPerPlant.reduce((sum, clumps) => sum + clumps.length, 0);
    const mesh = new THREE.InstancedMesh(
      lumpyCrownGeometry(),
      new THREE.MeshLambertMaterial({ color: 0xffffff, map: foliage() }),
      total,
    );
    let index = 0;
    shapes.forEach(({ item, shape }, plant) => {
      const { lightness, hueShift } = colourJitter(item.lon, item.lat);
      colour.setHex(item.type === "shrub" ? SHRUB_COLOUR : VITALITY_COLOURS[item.vitality]);
      colour.getHSL(hsl);
      const base = { h: hsl.h + hueShift, s: hsl.s, l: hsl.l * lightness };
      clumpsPerPlant[plant].forEach((clump, c) => {
        // Offsets in metres → world units. The world's axes run west/south (projectToWorld negates
        // both); the clumps' directions are random, so the sign does not matter here.
        const [x, y, z] = project(item.lon, item.lat, shape.crown.centreHeight + clump.dz);
        position.set(x + clump.dx * unitsPerMetre, y + clump.dy * unitsPerMetre, z);
        scale.set(clump.radius * unitsPerMetre, clump.radius * unitsPerMetre, clump.verticalRadius * unitsPerMetre);
        turn.setFromAxisAngle(zAxis, hash01(item.lon, item.lat, 3 + c) * Math.PI * 2);
        mesh.setMatrixAt(index, matrix.compose(position, turn, scale));
        colour.setHSL(base.h, base.s, Math.min(base.l * clump.shade, 0.9)).convertSRGBToLinear();
        mesh.setColorAt(index, colour);
        index += 1;
      });
    });
    mesh.instanceMatrix.needsUpdate = true;
    if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    // Crowns cast shadows (when the view turns them on); they do not receive any — a crown shading
    // itself through its own clumps looks like dirt, not like foliage.
    mesh.castShadow = true;
    group.add(mesh);
  }
  return group;
};
